import { useEffect, useState } from "react";
import { Loader2, Save, Hash, Coins, Landmark, FileText, Plus, X, Sparkles, CreditCard } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NUMBERING = [
  { key: "inquiry_numbering", label: "Inquiry", docType: "inquiry" },
  { key: "quotation_numbering", label: "Quotation", docType: "quotation" },
  { key: "proforma_numbering", label: "Proforma Invoice", docType: "proforma_invoice" },
  { key: "booking_numbering", label: "Booking", docType: "booking" },
  { key: "invoice_numbering", label: "Invoice", docType: "invoice" },
  { key: "payment_receipt_numbering", label: "Payment Receipt", docType: "payment_receipt" },
];

export default function CompanyConfig() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("company_config.edit");
  const canPropose = hasPermission("ai.tools.execute");
  const [cfg, setCfg] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [newCurrency, setNewCurrency] = useState("");
  const [previews, setPreviews] = useState({});
  const canViewBanks = hasPermission("bank_account.view");

  const previewType = async (docType, nc) => {
    try {
      const r = await api.post("/numbering/preview", { doc_type: docType, customer_code: "KT", config: nc });
      setPreviews((p) => ({ ...p, [docType]: r.data.number }));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    api.get("/companies/me/config").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    if (canViewBanks) api.get("/bank-accounts").then((r) => setBanks(r.data)).catch(() => setBanks([]));
  }, []); // eslint-disable-line

  const proposeAI = async () => {
    setProposing(true);
    try {
      await api.post("/ai/propose", {
        tool_key: "update_company_config",
        args: { terms_and_conditions: cfg.terms_and_conditions, payment_instructions: cfg.payment_instructions },
      });
      toast.success("AI proposal submitted — pending human approval");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setProposing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        inquiry_numbering: cfg.inquiry_numbering,
        quotation_numbering: cfg.quotation_numbering,
        proforma_numbering: cfg.proforma_numbering,
        booking_numbering: cfg.booking_numbering,
        invoice_numbering: cfg.invoice_numbering,
        payment_receipt_numbering: cfg.payment_receipt_numbering,
        supported_currencies: cfg.supported_currencies,
        payment_instructions: cfg.payment_instructions,
        terms_and_conditions: cfg.terms_and_conditions,
        default_bank_account_id: cfg.default_bank_account_id || "",
      };
      const { data } = await api.patch("/companies/me/config", payload);
      setCfg(data);
      toast.success("Configuration saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setNum = (nk, field, val) => setCfg((c) => ({ ...c, [nk]: { ...c[nk], [field]: val } }));

  const addCurrency = () => {
    const code = newCurrency.trim().toUpperCase();
    if (!code) return;
    if (cfg.supported_currencies.includes(code)) return toast.error("Currency already added");
    setCfg((c) => ({ ...c, supported_currencies: [...c.supported_currencies, code] }));
    setNewCurrency("");
  };

  const removeCurrency = (code) => setCfg((c) => ({ ...c, supported_currencies: c.supported_currencies.filter((x) => x !== code) }));

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;
  if (!cfg) return null;

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="company-config-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Company Foundation</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Company Configuration</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Document numbering, currencies and default operational text.</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {canPropose && (
              <button onClick={proposeAI} disabled={proposing} data-testid="ai-propose-button" className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60 dark:border-ink-gold/30 dark:bg-ink-gold/10 dark:text-ink-gold">
                {proposing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Propose via AI
              </button>
            )}
            <button onClick={save} disabled={saving} data-testid="config-save-button" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
            </button>
          </div>
        )}
      </div>

      {/* Numbering */}
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center gap-2">
          <Hash className="h-4 w-4 text-sky-600" />
          <h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Sequence Numbering</h2>
        </div>
        <div className="space-y-4">
          {NUMBERING.map((n) => {
            const nc = cfg[n.key] || {};
            return (
              <div key={n.key} className="rounded-lg border border-slate-100 p-3 dark:border-ink-line" data-testid={`numbering-${n.key}`}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{n.label}</span>
                  <button type="button" onClick={() => previewType(n.docType, nc)} data-testid={`numbering-${n.key}-preview-btn`} className="rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300 dark:hover:bg-ink-panel">
                    Preview next
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">Prefix</label>
                    <input value={nc.prefix ?? ""} disabled={!canEdit} onChange={(e) => setNum(n.key, "prefix", e.target.value)} data-testid={`numbering-${n.key}-prefix`} className="w-full rounded-md border border-slate-200 px-2 py-1.5 font-mono-tf text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">Format (tokens: prefix, customer, year, period, day, seq)</label>
                    <input value={nc.number_format ?? ""} disabled={!canEdit} onChange={(e) => setNum(n.key, "number_format", e.target.value)} data-testid={`numbering-${n.key}-format`} className="w-full rounded-md border border-slate-200 px-2 py-1.5 font-mono-tf text-xs dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">Next #</label>
                    <input type="number" value={nc.next_sequence ?? 1} disabled={!canEdit} onChange={(e) => setNum(n.key, "next_sequence", parseInt(e.target.value || "1", 10))} data-testid={`numbering-${n.key}-next`} className="w-full rounded-md border border-slate-200 px-2 py-1.5 font-mono-tf text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">Sequence scope</label>
                    <select value={nc.seq_scope ?? "yearly"} disabled={!canEdit} onChange={(e) => setNum(n.key, "seq_scope", e.target.value)} data-testid={`numbering-${n.key}-scope`} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text">
                      <option value="company">company (continuous)</option>
                      <option value="yearly">yearly</option>
                      <option value="daily">daily</option>
                      <option value="customer_daily">customer + day</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">Period token</label>
                    <select value={nc.period_mode ?? "none"} disabled={!canEdit} onChange={(e) => setNum(n.key, "period_mode", e.target.value)} data-testid={`numbering-${n.key}-period`} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text">
                      <option value="none">none</option>
                      <option value="month">month (09)</option>
                      <option value="month_roman">month roman (IX)</option>
                      <option value="custom">custom token</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">Custom period token</label>
                    <input value={nc.period_custom_token ?? ""} disabled={!canEdit || (nc.period_mode ?? "none") !== "custom"} onChange={(e) => setNum(n.key, "period_custom_token", e.target.value)} placeholder="e.g. IIX" data-testid={`numbering-${n.key}-period-token`} className="w-full rounded-md border border-slate-200 px-2 py-1.5 font-mono-tf text-sm disabled:opacity-50 dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">Seq padding</label>
                    <input type="number" min="1" max="8" value={nc.seq_padding ?? 4} disabled={!canEdit} onChange={(e) => setNum(n.key, "seq_padding", parseInt(e.target.value || "4", 10))} data-testid={`numbering-${n.key}-padding`} className="w-full rounded-md border border-slate-200 px-2 py-1.5 font-mono-tf text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
                  </div>
                </div>
                {previews[n.docType] && (
                  <div className="mt-2 text-xs text-slate-500 dark:text-slate-400" data-testid={`numbering-${n.key}-preview`}>
                    Next (not consumed): <span className="font-mono-tf font-semibold text-slate-700 dark:text-ink-gold">{previews[n.docType]}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Currencies */}
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center gap-2">
          <Coins className="h-4 w-4 text-amber-600" />
          <h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Supported Currencies</h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="currency-list">
          {cfg.supported_currencies.map((c) => (
            <span key={c} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-mono-tf text-sm font-semibold text-slate-700 dark:border-ink-line dark:bg-ink-panel dark:text-slate-200">
              {c}
              {canEdit && (
                <button onClick={() => removeCurrency(c)} data-testid={`currency-remove-${c}`} className="text-slate-400 hover:text-rose-500">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
        {canEdit && (
          <div className="mt-3 flex gap-2">
            <input value={newCurrency} onChange={(e) => setNewCurrency(e.target.value)} placeholder="e.g. JPY" data-testid="currency-input" className="w-32 rounded-md border border-slate-200 px-3 py-1.5 text-sm uppercase dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
            <button onClick={addCurrency} data-testid="currency-add-button" className="flex items-center gap-1 rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        )}
      </section>

      {/* Payment instructions & terms */}
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center gap-2">
          <Landmark className="h-4 w-4 text-emerald-600" />
          <h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Payment Instructions</h2>
        </div>
        <div className="mb-4 rounded-lg border border-slate-200 p-4 dark:border-ink-line" data-testid="default-payment-account">
          <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-400">
            <CreditCard className="h-3.5 w-3.5" /> Default Payment Account
          </label>
          <select value={cfg.default_bank_account_id || ""} disabled={!canEdit || !canViewBanks}
            onChange={(e) => setCfg((c) => ({ ...c, default_bank_account_id: e.target.value }))}
            data-testid="default-bank-account-select"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50 dark:border-ink-line dark:bg-ink-panel dark:text-ink-text">
            <option value="">None — use free-text instructions below</option>
            {banks.filter((b) => b.status === "active").map((b) => (
              <option key={b.id} value={b.id} data-testid={`default-bank-option-${b.id}`}>
                {b.bank_name} — {b.account_name} — {b.currency} — {b.account_number_masked}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-[11px] text-slate-400">Only the account reference is stored — future financial documents resolve live bank details from the Bank Account Master. Inactive accounts are not selectable.</p>
          {cfg.default_bank_account_id && !banks.some((b) => b.id === cfg.default_bank_account_id && b.status === "active") && (
            <p className="mt-1.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400" data-testid="default-bank-inactive-warning">The current default account is inactive or was removed — select an active account or None.</p>
          )}
        </div>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Free-text instructions (fallback)</label>
        <textarea value={cfg.payment_instructions} disabled={!canEdit} onChange={(e) => setCfg((c) => ({ ...c, payment_instructions: e.target.value }))} rows={3} data-testid="payment-instructions-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
        <div className="mb-4 mt-6 flex items-center gap-2">
          <FileText className="h-4 w-4 text-indigo-600 dark:text-ink-gold" />
          <h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Terms &amp; Conditions</h2>
        </div>
        <textarea value={cfg.terms_and_conditions} disabled={!canEdit} onChange={(e) => setCfg((c) => ({ ...c, terms_and_conditions: e.target.value }))} rows={4} data-testid="terms-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text" />
      </section>
    </div>
  );
}
